/**
 * osintReport.ts — human-readable Instagram OSINT lead report.
 *
 * The `.manifest.ndjson` download is the machine-verifiable, signed chain-of-
 * custody record — great for audit, unreadable for a human. This builds the
 * OTHER half: a clean HTML document (print → PDF) with a plain-English
 * "What was found" narrative so an officer can understand a run at a glance
 * instead of parsing JSON. Reuses the shared report scaffolding (R21).
 */
import type { InstagramOsintRunResult, InstagramArtifact, InstagramMediaResult } from '../services/api';
import {
    esc,
    fmtTs,
    monoWrap,
    wrapReportHtml,
    buildReportMasthead,
    buildReportToolbar,
    buildReportShell,
    wrapReportSection,
    openReportHtmlInNewTab,
    type ReportTocEntry,
} from './reportCommon';

const ARTIFACT_LABELS: Record<string, string> = {
    crypto_address: 'crypto address',
    tx_hash: 'transaction hash',
    upi_id: 'UPI ID',
    upi_payee_name: 'UPI payee name',
    phone: 'phone number',
    email: 'email address',
    handle: 'social handle',
    url: 'link',
    qr_payload: 'QR payload',
    utr: 'UTR/RRN',
    ifsc: 'IFSC code',
    bank_account: 'bank account',
    pan: 'PAN',
};

const SURFACE_LABELS: Record<string, string> = {
    bio: 'profile bio', post: 'a post', reel: 'a reel', profile: 'the profile',
};

const SURFACE_SHORT: Record<string, string> = {
    bio: 'Bio', post: 'Post', reel: 'Reel', profile: 'Profile',
};

const PRE_BLOCK = 'white-space:pre-wrap;background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;padding:8px;font-size:11px';

/** If a URL is an Instagram/Facebook redirect shim, return the real destination
 *  host (e.g. l.instagram.com/?u=…okx.com → "okx.com"). Otherwise the plain host. */
function destinationHost(raw: string): string | null {
    try {
        const u = new URL(raw);
        const shim = /(^|\.)(l\.instagram\.com|l\.facebook\.com|lm\.facebook\.com)$/i.test(u.hostname);
        if (shim) {
            const target = u.searchParams.get('u');
            if (target) return new URL(decodeURIComponent(target)).hostname.replace(/^www\./, '');
        }
        return u.hostname.replace(/^www\./, '');
    } catch {
        return null;
    }
}

function listJoin(items: string[]): string {
    const a = items.map((s) => esc(s));
    if (a.length <= 1) return a.join('');
    if (a.length === 2) return `${a[0]} and ${a[1]}`;
    return `${a.slice(0, -1).join(', ')}, and ${a[a.length - 1]}`;
}

function groupByType(artifacts: InstagramArtifact[]): Map<string, InstagramArtifact[]> {
    const m = new Map<string, InstagramArtifact[]>();
    for (const a of artifacts) {
        const arr = m.get(a.artifact_type) || [];
        arr.push(a);
        m.set(a.artifact_type, arr);
    }
    return m;
}

function truncateHash(hash: string | null | undefined, head = 10, tail = 6): string {
    if (!hash) return '—';
    if (hash.length <= head + tail + 1) return hash;
    return `${hash.slice(0, head)}…${hash.slice(-tail)}`;
}

function truncateContext(text: string | null | undefined, max = 120): string {
    if (!text) return '—';
    const t = text.replace(/\s+/g, ' ').trim();
    if (t.length <= max) return t;
    return `${t.slice(0, max)}…`;
}

function artifactLabel(type: string): string {
    return ARTIFACT_LABELS[type] || type.replace(/_/g, ' ');
}

/** CSS layered on top of REPORT_CSS for the Instagram leads printable report. */
const OSINT_REPORT_CSS = `
.page { max-width: 1040px; }
table.data.findings { table-layout: fixed; }
table.data.findings th:nth-child(1),
table.data.findings td:nth-child(1) { width: 12%; }
table.data.findings th:nth-child(2),
table.data.findings td:nth-child(2) { width: 38%; }
table.data.findings th:nth-child(3),
table.data.findings td:nth-child(3) { width: 14%; }
table.data.findings th:nth-child(4),
table.data.findings td:nth-child(4) { width: 16%; }
table.data.findings th:nth-child(5),
table.data.findings td:nth-child(5) { width: 20%; }
table.data.findings td.finding-value {
    font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace;
    font-size: 11px;
    overflow-wrap: anywhere;
    word-break: normal;
    line-height: 1.45;
}
table.data.findings td.finding-meta {
    font-size: 10px;
    line-height: 1.4;
    vertical-align: top;
}
table.data.findings .meta-line { display: block; }
table.data.findings td.finding-context {
    font-size: 10px;
    color: #64748b;
    overflow-wrap: anywhere;
    word-break: normal;
    line-height: 1.4;
}
@media print {
    .page { max-width: none; padding: 16px; }
    @page { size: A4 landscape; margin: 12mm 10mm; }
}
`;

/** One plain-English sentence describing a group of same-type leads. */
function describeType(type: string, items: InstagramArtifact[]): string {
    const n = items.length;
    const label = artifactLabel(type);
    const plural = n > 1;
    if (type === 'url') {
        const dests = [...new Set(items.map((a) => destinationHost(a.value)).filter(Boolean) as string[])];
        return `<strong>${n} ${plural ? 'links' : 'link'}</strong> in the profile`
            + (dests.length ? `, pointing to ${listJoin(dests)}` : '') + '.';
    }
    if (type === 'crypto_address') {
        return `<strong>${n} cryptocurrency wallet ${plural ? 'addresses' : 'address'}</strong>: `
            + items.map((a) => `<span class="mono">${monoWrap(a.value)}</span>${a.chain ? ` (${esc(a.chain)})` : ''}`).join(', ') + '.';
    }
    const values = items.map((a) => `<span class="mono">${monoWrap(a.value)}</span>`).join(', ');
    return `<strong>${n} ${label}${plural ? 's' : ''}</strong>: ${values}.`;
}

/** The "What was found" narrative — the descriptive, non-JSON summary. */
function buildNarrative(r: InstagramOsintRunResult): string {
    const parts: string[] = [];
    const mode = r.committed
        ? 'persisted mode (a signed manifest was generated)'
        : 'preview-only mode (not persisted)';
    parts.push(`This report summarises an Instagram OSINT run on the handle `
        + `<strong>@${esc(r.suspect_handle)}</strong>, run in <strong>${mode}</strong>.`);

    const valid = r.artifacts.filter((a) => a.valid);
    const total = r.artifacts.length;
    if (total === 0) {
        parts.push('No leads were extracted from the supplied inputs.');
    } else {
        const surfaces = Object.keys(r.by_surface || {}).map((s) => SURFACE_LABELS[s] || s);
        parts.push(`A total of <strong>${total}</strong> lead${total > 1 ? 's' : ''} `
            + `${total > 1 ? 'were' : 'was'} extracted`
            + (surfaces.length ? ` from ${listJoin(surfaces)}` : '')
            + (valid.length < total ? `, of which ${valid.length} ${valid.length === 1 ? 'is' : 'are'} valid` : '')
            + '.');
        for (const [type, items] of groupByType(valid)) {
            parts.push(describeType(type, items));
        }
    }

    if (r.media.length) {
        const qr = r.media.reduce((sum, m) => sum + m.ocr.qr_payloads.length, 0);
        parts.push(`${r.media.length} media file${r.media.length > 1 ? 's' : ''} `
            + `${r.media.length > 1 ? 'were' : 'was'} processed with OCR`
            + (qr ? `; ${qr} QR code${qr > 1 ? 's' : ''} decoded` : '') + '.');
    }

    parts.push('<em>All items above are Tier-4 OSINT leads — investigative starting points, '
        + 'not confirmed facts. The accompanying signed manifest (.ndjson) is the tamper-evident '
        + 'evidence record for chain of custody.</em>');

    return wrapReportSection('summary', 'Summary — What was found',
        parts.map((p) => `<p style="margin-bottom:8px">${p}</p>`).join(''));
}

function buildMetaBox(r: InstagramOsintRunResult): string {
    const generated = fmtTs(new Date().toISOString());
    const rows: [string, string][] = [
        ['Handle', `@${esc(r.suspect_handle)}`],
        ['Case ID', esc(r.case_id)],
        ['Run mode', r.committed ? 'Persisted (signed manifest)' : 'Preview only'],
    ];
    if (r.run_id) rows.push(['Run ID', esc(r.run_id)]);
    rows.push(['Leads', String(r.artifacts.length)]);
    rows.push(['Media', String(r.media.length)]);
    rows.push(['Generated', esc(generated)]);
    const body = rows.map(([k, v]) =>
        `<tr><td class="label">${k}</td><td class="value mono">${v}</td></tr>`).join('');
    return `<div class="meta-box"><h2>Run details</h2><table class="meta-table">${body}</table></div>`;
}

function buildCountsSection(r: InstagramOsintRunResult): string {
    const typeEntries = Object.entries(r.counts || {}).filter(
        ([k, v]) => !['total', 'valid', 'rejected'].includes(k) && v > 0,
    );
    const surfaceEntries = Object.entries(r.by_surface || {}).filter(([, v]) => v > 0);
    if (!typeEntries.length && !surfaceEntries.length) return '';

    const typeRows = typeEntries.length
        ? typeEntries.map(([k, v]) =>
            `<tr><td>${esc(artifactLabel(k))}</td><td class="mono">${v}</td></tr>`).join('')
        : '<tr><td colspan="2" class="muted">No type counts</td></tr>';
    const surfaceRows = surfaceEntries.length
        ? surfaceEntries.map(([k, v]) =>
            `<tr><td>${esc(SURFACE_SHORT[k] || k)}</td><td class="mono">${v}</td></tr>`).join('')
        : '<tr><td colspan="2" class="muted">No surface counts</td></tr>';

    return wrapReportSection('lead-counts', 'Lead counts',
        `<div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">`
        + `<div><h3 style="font-size:12px;margin:0 0 6px">By type</h3>`
        + `<table class="data"><thead><tr><th>Type</th><th>Count</th></tr></thead>`
        + `<tbody>${typeRows}</tbody></table></div>`
        + `<div><h3 style="font-size:12px;margin:0 0 6px">By surface</h3>`
        + `<table class="data"><thead><tr><th>Surface</th><th>Count</th></tr></thead>`
        + `<tbody>${surfaceRows}</tbody></table></div>`
        + `</div>`);
}

function buildWarningsSection(r: InstagramOsintRunResult): string {
    if (!r.warnings?.length) return '';
    const items = r.warnings.map((w) => `<li>${esc(w)}</li>`).join('');
    return wrapReportSection('warnings', 'Warnings',
        `<ul style="color:#b45309;margin:0;padding-left:18px">${items}</ul>`);
}

function buildFindingsTable(r: InstagramOsintRunResult): string {
    if (!r.artifacts.length) {
        return wrapReportSection('findings', 'Findings',
            '<p class="muted">No artefacts extracted.</p>');
    }
    const sorted = [...r.artifacts].sort((a, b) => Number(b.valid) - Number(a.valid));
    const rows = sorted.map((a) => {
        const dest = a.artifact_type === 'url' ? destinationHost(a.value) : null;
        // Value gets ~38% width + soft-break opportunities so wallets/emails stay readable.
        const value = dest
            ? `<strong>${esc(dest)}</strong><div class="muted" style="font-size:10px">${monoWrap(a.value)}</div>`
            : monoWrap(a.value);
        const status = a.valid
            ? '<span style="color:#16a34a;font-weight:600">valid</span>'
            : `<span style="color:#dc2626">rejected${a.rejected_reason ? ` (${esc(a.rejected_reason)})` : ''}</span>`;
        const surface = esc(
            SURFACE_SHORT[a.source_surface]
            || SURFACE_LABELS[a.source_surface]
            || a.source_surface
            || '—',
        );
        const chain = a.chain ? esc(a.chain) : '—';
        const source = a.source_url
            ? `<a href="${esc(a.source_url)}" target="_blank" rel="noreferrer">source</a>`
            : '—';
        const ocrBits: string[] = [];
        if (a.ocr_confidence != null) ocrBits.push(`${(a.ocr_confidence * 100).toFixed(0)}%`);
        if (a.source_language) ocrBits.push(a.source_language);
        const ocrMeta = ocrBits.length ? esc(ocrBits.join(' · ')) : null;
        const hash = a.content_hash ? monoWrap(truncateHash(a.content_hash), 12) : null;
        const classification = `<span class="meta-line">${chain} · ${surface} · ${status}</span>`;
        const provenance = [
            `<span class="meta-line">${source} · ${esc(a.extractor || '—')}</span>`,
            ocrMeta ? `<span class="meta-line muted">${ocrMeta}</span>` : '',
            hash ? `<span class="meta-line muted mono">${hash}</span>` : '',
        ].join('');
        const context = a.raw_context
            ? esc(truncateContext(a.raw_context, 160))
            : '—';
        return `<tr${a.valid ? '' : ' class="muted"'}>`
            + `<td>${esc(artifactLabel(a.artifact_type))}</td>`
            + `<td class="finding-value">${value}</td>`
            + `<td class="finding-meta">${classification}</td>`
            + `<td class="finding-meta">${provenance}</td>`
            + `<td class="finding-context">${context}</td>`
            + `</tr>`;
    }).join('');
    return wrapReportSection('findings', `Findings (${r.artifacts.length})`,
        `<table class="data findings"><thead><tr>`
        + `<th>Type</th><th>Value</th><th>Chain / Surface / Status</th>`
        + `<th>Provenance</th><th>Context</th>`
        + `</tr></thead><tbody>${rows}</tbody></table>`);
}

function buildMediaArtifactList(artifacts: InstagramArtifact[]): string {
    if (!artifacts.length) return '';
    const rows = artifacts.map((a) => {
        const status = a.valid
            ? '<span style="color:#16a34a">valid</span>'
            : `<span style="color:#dc2626">rejected</span>`;
        return `<tr>`
            + `<td>${esc(artifactLabel(a.artifact_type))}</td>`
            + `<td class="finding-value">${monoWrap(a.value)}</td>`
            + `<td>${a.chain ? esc(a.chain) : '—'}</td>`
            + `<td>${status}</td>`
            + `</tr>`;
    }).join('');
    return `<p style="margin-top:8px"><strong>Leads from this media (${artifacts.length}):</strong></p>`
        + `<table class="data"><thead><tr>`
        + `<th>Type</th><th>Value</th><th>Chain</th><th>Status</th>`
        + `</tr></thead><tbody>${rows}</tbody></table>`;
}

function buildMediaSection(m: InstagramMediaResult): string {
    const metaRows: [string, string][] = [];
    if (m.caption) metaRows.push(['Caption', esc(m.caption)]);
    if (m.source_url) {
        metaRows.push(['Source URL', `<a href="${esc(m.source_url)}" target="_blank" rel="noreferrer">${esc(m.source_url)}</a>`]);
    }
    if (m.content_hash) metaRows.push(['Content hash', `<span class="mono">${monoWrap(m.content_hash)}</span>`]);
    const ocrBits = [
        esc(m.ocr.engine),
        m.ocr.detected_language ? esc(m.ocr.detected_language) : null,
        m.ocr.confidence != null ? `confidence ${(m.ocr.confidence * 100).toFixed(0)}%` : null,
        m.ocr.translator ? `translator ${esc(m.ocr.translator)}` : null,
    ].filter(Boolean);
    metaRows.push(['OCR', ocrBits.join(' · ')]);
    if (m.ocr.notes.length) metaRows.push(['Notes', esc(m.ocr.notes.join(', '))]);

    const metaTable = metaRows.length
        ? `<table class="meta-table" style="margin-bottom:8px">${metaRows.map(([k, v]) =>
            `<tr><td class="label">${k}</td><td class="value">${v}</td></tr>`).join('')}</table>`
        : '';

    const qr = m.ocr.qr_payloads.length
        ? `<p><strong>QR codes (${m.ocr.qr_payloads.length}):</strong></p>`
          + `<ul>${m.ocr.qr_payloads.map((q) => `<li class="mono">${monoWrap(q)}</li>`).join('')}</ul>`
        : '';

    const original = m.ocr.ocr_text_original
        ? `<p style="margin-top:6px"><strong>OCR text (original):</strong></p>`
          + `<pre style="${PRE_BLOCK}">${esc(m.ocr.ocr_text_original)}</pre>`
        : '';

    const english = m.ocr.ocr_text_english
        && m.ocr.ocr_text_english !== m.ocr.ocr_text_original
        ? `<p style="margin-top:6px"><strong>Translation (English):</strong></p>`
          + `<pre style="${PRE_BLOCK}">${esc(m.ocr.ocr_text_english)}</pre>`
        : '';

    const consolidated = m.consolidated_text
        && m.consolidated_text !== m.ocr.ocr_text_original
        && m.consolidated_text !== m.ocr.ocr_text_english
        ? `<p style="margin-top:6px"><strong>Consolidated text:</strong></p>`
          + `<pre style="${PRE_BLOCK}">${esc(m.consolidated_text)}</pre>`
        : '';

    return wrapReportSection(`media-${m.media_index}`, `Media ${m.media_index + 1}: ${m.filename}`,
        metaTable + qr + original + english + consolidated + buildMediaArtifactList(m.artifacts || []));
}

function buildMediaSections(r: InstagramOsintRunResult): string {
    if (!r.media.length) return '';
    return r.media.map(buildMediaSection).join('');
}

export function buildOsintReportHtml(r: InstagramOsintRunResult): string {
    const title = 'Instagram leads report';
    const subtitle = `Suspect leads · @${r.suspect_handle || 'preview'}`;
    const filename = `instagram_leads_${(r.suspect_handle || 'preview').replace(/[^a-z0-9]/gi, '_')}_report`;

    const toc: ReportTocEntry[] = [
        { id: 'summary', label: 'Summary' },
    ];
    if (Object.keys(r.counts || {}).length || Object.keys(r.by_surface || {}).length) {
        toc.push({ id: 'lead-counts', label: 'Lead counts' });
    }
    if (r.warnings?.length) toc.push({ id: 'warnings', label: 'Warnings' });
    toc.push({ id: 'findings', label: 'Findings' });
    for (const m of r.media || []) {
        toc.push({ id: `media-${m.media_index}`, label: `Media ${m.media_index + 1}` });
    }

    const main = buildReportMasthead({ title, subtitle })
        + buildMetaBox(r)
        + buildCountsSection(r)
        + buildNarrative(r)
        + buildWarningsSection(r)
        + buildFindingsTable(r)
        + buildMediaSections(r)
        + `<div class="muted" style="font-size:10px;margin-top:16px">`
        + `Generated by Outris · Instagram leads (OSINT). Treat as unverified `
        + `investigative starting points. Report generated ${esc(fmtTs(new Date().toISOString()))}.</div>`;

    return wrapReportHtml(
        buildReportToolbar(title, filename) + buildReportShell(main, toc),
        title,
        OSINT_REPORT_CSS,
    );
}

/** Open the human-readable report in a new tab (print → PDF from the toolbar). */
export function openOsintReport(r: InstagramOsintRunResult): void {
    openReportHtmlInNewTab(buildOsintReportHtml(r));
}
